import { setAttr } from '@utils';
import { memo, useState } from 'react';
import { Text, Filter } from '@components/index';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { updateFilterSearch } from '@store/redux/features/filter';
import { getManufacturersAutocomplete } from '@services/queries/manufacturers';
import { useMutation } from 'react-query';
import { toggleOn } from '@store/redux/features/mixpanel';
import { useSetState } from '@mantine/hooks';
import { InventoryAutocompleteItems } from '@components/SearchBars/SearchBarExpansiveWithHistory/InventoryAutocompleteItems';
import { getLastWord } from '@utils/getLastWord/getLastWord';
import { ReactComponent as Menu } from '@assets/svg/menu.svg';
import {
  updateRecentSearches,
  VIEW_LIMIT,
} from '@store/redux/features/search/searchSlice';
import { removeLastWord } from '@utils/removeLastWord/removeLastWord';
import { deviceTypes } from '@styles/mediaQueries';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { useNavigationState } from '@store/hookstate';
import { isMassEditing } from './logic/isMassEditing';
import { SearchProducts } from './Products.styles';
import { HeadingProps, HeadingType, IFilterOpts } from './Heading.types';
import { makeSearchFunc } from './logic/makeSearchFunc';
import { makeWord } from './logic/makeWord';
import { SEARCH_INVENTORY_KEY } from './Products';
import { ExtraMenuOptions } from './ExtraMenuOptions';
import { MassEditOptions } from './MassEditOptions';

function Heading(props: HeadingProps) {
  const { state, send, resultCount, isLoading, startSearch, ...rest } = props;

  const { isMobile, isDesktop } = useSelector(checkDevice());

  const { filter, search } = useAppSelector();
  const dispatch = useAppDispatch();

  const [searchData, setSearchData] = useSetState<IFilterOpts>({
    recentSearches: [],
    recentViewed: [],
    autocompleteOptions: [],
  });

  const mutationGetManufactorAutocomplete = useMutation(
    async (text: string) => {
      return getManufacturersAutocomplete(getLastWord(text));
    },
    {
      onSuccess: (result, text) => {
        const prefix = removeLastWord(text);
        setSearchData({
          autocompleteOptions: result.slice(0, VIEW_LIMIT).map(item => {
            return {
              labelOne: makeWord(prefix, item.name),
              id: item.id,
            };
          }),
        });
      },
    },
  );

  function onClear() {
    dispatch(updateFilterSearch(''));
    startSearch();
  }
  function onSearch(input: string) {
    dispatch(updateFilterSearch(input));
    dispatch(toggleOn('Inventory Search'));
    dispatch(
      updateRecentSearches({
        keyHistory: SEARCH_INVENTORY_KEY,
        value: input,
      }),
    );
    startSearch();
  }
  const [searchValue, setSearch] = useState('');

  const searchFunc = makeSearchFunc({
    search,
    broadcast: setSearchData,
    setSearch,
    mutationGetManufactorAutocomplete,
  });
  function onSearchClick() {
    return () => {
      searchFunc(searchValue);
    };
  }

  const navigationState = useNavigationState();

  const showMassEditOpts = isMassEditing(state);

  return (
    <div {...rest}>
      <div className="TitleAndFilter">
        <div className="TitleAndMenu">
          {isMobile && (
            <BActionIcon
              className="MenuIcon"
              variant="transparent"
              onClick={navigationState.openNavigation}
            >
              <Menu />
            </BActionIcon>
          )}
          <Text text={isMobile ? 'p200' : 'h800'} className="Title">
            Inventory
          </Text>
        </div>
        <div className="FilterAndSearch">
          <SearchProducts
            value={searchValue}
            startSearch={onSearch}
            onChange={searchFunc}
            onClick={onSearchClick()}
            onClear={onClear}
            loading={isLoading}
            placeholder="Search Products"
            autocompleteItems={autocompleteProps => (
              <InventoryAutocompleteItems
                autocompleteProps={autocompleteProps}
                filterOpts={searchData}
                broadcastFilterOpts={setSearchData}
              />
            )}
          />
          <Filter filterData={filter.options} startSearch={startSearch} />
          {isDesktop && <ExtraMenuOptions state={state} send={send} />}
        </div>
      </div>
      {isDesktop && showMassEditOpts && (
        <MassEditOptions state={state} send={send} />
      )}

      <div className="Results" hidden={setAttr(resultCount === -1)}>
        <Text text={isMobile ? 'h300' : 'h400'}>{resultCount}</Text>
        <Text inline text="pl100" span>
          Results
        </Text>
      </div>
    </div>
  );
}
export default memo(styled(Heading)`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  border-bottom: 1px solid var(--beam-color-neutral-n300);
  padding-bottom: 15px;

  .TitleAndFilter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 16px;

    [data-device='${deviceTypes.mobile}'] & {
      row-gap: 16px;
      flex-wrap: wrap;
    }

    .MenuIcon {
      padding-left: 0;
      padding-right: 16px;
      width: 32px;
      height: 32px;
    }
  }

  .FilterAndSearch {
    display: flex;
    align-items: center;
    column-gap: 8px;

    flex-grow: 1;
    justify-content: end;
    [data-device='${deviceTypes.mobile}'] & {
      justify-content: center;
    }
  }
  [data-device='${deviceTypes.mobile}'] & {
    .TitleAndMenu {
      width: 100%;
      display: grid;
      align-items: center;
      grid-template-columns: 32px 1fr 32px;
      column-gap: 16px;

      padding: 15px 0px 7px;
    }
    .TitleAndMenu > .Title {
      color: var(--beam-color-neutral-n600);
      text-align: center;
    }
  }

  .Results {
    display: flex;
    align-items: center;
    column-gap: 3px;
  }
`) as HeadingType;
